<template>
	<Dialog
		:dialog-visible="dialog"
		:agree-text="requested ? 'Done' : 'Send Request'"
		:cancel-text="requested ? '' : 'Cancel'"
		:remove-cancel-button="requested"
		:disable-agree-button="false"
		:agree-loading="submitting"
		:persistent-dialog="submitting"
		:options="{ width: '460px' }"
		@agree="requested ? close() : submit()"
		@cancel="close">
		<template #dialog-header="{ titleId }">
			<h5 :id="titleId" v-test="'request-access-title'">Ask to edit</h5>
		</template>
		<template #dialog-body>
			<div v-if="!requested">
				<div class="text-body-2 text-grey-darken-2 mb-4">
					Let the project owner know you'd like edit access. We'll email them your address and your note.
				</div>
				<EmailTextField
					v-model="email"
					v-test="'request-access-email'"
					label="Your email"
					placeholder="name@example.com"
					:rules="emailRules"
					:readonly="submitting"
					class="mb-3" />
				<v-textarea
					v-model="message"
					v-test="'request-access-message'"
					label="Add a note (optional)"
					placeholder="Hi! I'd like to help edit this project."
					:counter="500"
					:rules="messageRules"
					:readonly="submitting"
					rows="3" />
				<div v-if="submitting" role="status" class="text-body-2 mt-2">Sending your request…</div>
				<v-alert v-else-if="errorMessage" type="error" variant="tonal" density="compact" class="mt-2">{{ errorMessage }}</v-alert>
			</div>
			<div v-else class="d-flex flex-column align-center text-center py-4" v-test="'request-access-done'">
				<v-icon size="56" color="primary" class="mb-3">mdi-check-circle-outline</v-icon>
				<div class="text-body-1">Your request is on its way.</div>
				<div class="text-body-2 text-grey-darken-2 mt-1">The owner will get an email and can grant you access.</div>
			</div>
		</template>
	</Dialog>
</template>

<script>
	import Dialog from "./Dialog.vue";
	import api from "../api";
	import { countCharacters, getMessageFromError } from "@/helpers/utilities";
	import { couldNot } from "@/helpers/userMessages";
	import { VuetifyValidation } from "@/helpers/Validation";

	const EMAIL_ERROR = "Please enter a valid email address.";
	const emailRule = VuetifyValidation.EmailValidation(EMAIL_ERROR).isValid;
	const MESSAGE_MAX = 500;

	export default {
		name: "RequestAccessDialog",
		components: { Dialog },
		props: {
			projectId: { type: String, required: true },
			// The project's public shareId — the server-side possession proof for the request (review #9).
			shareId: { type: String, required: true },
			// LSP-17767 testing follow-up: a SIGNED-IN viewer (e.g. a teammate on view-only access)
			// shouldn't have to retype their own address — the parent passes the session email and
			// open() prefills it. Stays editable: the field identifies where the grant should go,
			// and a requester may legitimately want a different address. Guests pass nothing.
			prefillEmail: { type: String, default: "" }
		},
		data() {
			return {
				dialog: false,
				requested: false,
				submitting: false,
				email: "",
				message: "",
				errorMessage: "",
				emailRules: [(value) => !!value?.trim() || EMAIL_ERROR],
				// Warn, never hard-cap — per repo CLAUDE.md. Grapheme-aware count (countCharacters).
				messageRules: [(value) => countCharacters(value || "") <= MESSAGE_MAX || `Maximum ${MESSAGE_MAX} characters`]
			};
		},
		watch: {
			message() {
				this.errorMessage = "";
			},
			email() {
				this.errorMessage = "";
			}
		},
		methods: {
			open() {
				if (this.submitting) {
					console.warn("RequestAccessDialog: ignored open while sending", { projectId: this.projectId });
					return;
				}
				this.requested = false;
				this.email = (this.prefillEmail || "").trim();
				this.message = "";
				this.errorMessage = "";
				this.dialog = true;
			},
			close() {
				if (this.submitting) {
					console.warn("RequestAccessDialog: ignored dismissal while sending", { projectId: this.projectId });
					return;
				}
				this.dialog = false;
			},
			async submit() {
				console.info("RequestAccessDialog: submit", { projectId: this.projectId, submitting: this.submitting });
				// Always-enabled button, no-op when invalid (repo CLAUDE.md) — but surface the REAL
				// reason: an over-limit note must not report "invalid email".
				if (this.submitting) {
					console.warn("RequestAccessDialog: ignored duplicate submission", { projectId: this.projectId });
					return;
				}
				const email = this.email || "";
				if (!email || emailRule(email) !== true) {
					console.warn("RequestAccessDialog: invalid email", { projectId: this.projectId });
					this.errorMessage = EMAIL_ERROR;
					return;
				}
				if (countCharacters(this.message || "") > MESSAGE_MAX) {
					console.warn("RequestAccessDialog: note exceeds limit", { projectId: this.projectId, length: countCharacters(this.message) });
					this.errorMessage = `Please shorten your note to ${MESSAGE_MAX} characters or less.`;
					return;
				}
				this.submitting = true;
				this.errorMessage = "";
				try {
					await api.requestProjectAccess(this.projectId, this.shareId, email, this.message.trim());
					this.requested = true;
					console.info("RequestAccessDialog: request sent", { projectId: this.projectId });
				} catch (error) {
					const status = error?.httpStatus ?? error?.code;
					console.warn("RequestAccessDialog: request failed", { projectId: this.projectId, status, errorName: error?.name });
					const fallback = couldNot("send your request");
					this.errorMessage = error?.userFacing === true
						? getMessageFromError(error, fallback)
						: status === 429
							? "Please wait before sending another request. If you already requested access, the owner can respond to that request."
							: fallback;
				} finally {
					this.submitting = false;
				}
			}
		}
	};
</script>
